import React from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../../util/reactIntl';
import { Heading, NamedLink, ListingCard } from '../../../components';
import css from './FeatureGridPanel.module.css';

// Set to true to enable debug banner for testing personalization
const SHOW_DEBUG_INFO = false;

/**
 * FeatureGridPanel component that displays featured listings in a grid layout
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array} [props.gridItems] - Array of listing objects to display
 * @param {number} [props.columns] - Number of columns in the grid (default: 6)
 * @param {number} [props.rows] - Number of rows to display (default: 3)
 * @param {boolean} [props.isPersonalized] - Whether listings are personalized based on user search history
 * @param {Object} [props.debugInfo] - Debug info about personalization (for testing)
 * @returns {JSX.Element} Feature grid panel with listing cards
 */
const FeatureGridPanel = props => {
  const {
    className,
    rootClassName,
    gridItems = [],
    columns = 6,
    rows = 3,
    isPersonalized = false,
    debugInfo = null,
  } = props;

  // Take only the number of items that fit in the grid (filtering is done in the duck)
  const displayItems = gridItems.slice(0, columns * rows);

  const classes = classNames(rootClassName || css.root, className);

  // Don't render if no available items
  if (!displayItems || displayItems.length === 0) {
    return null;
  }

  // Use personalized title when listings are based on user search history
  const titleMessageId = isPersonalized
    ? 'FeatureGridPanel.titlePersonalized'
    : 'FeatureGridPanel.title';

  // Debug banner styles (inline for easy removal)
  const debugBannerStyle = {
    background: isPersonalized ? '#d4edda' : '#fff3cd',
    border: `1px solid ${isPersonalized ? '#c3e6cb' : '#ffc107'}`,
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
    fontSize: '13px',
    fontFamily: 'monospace',
  };

  return (
    <div className={classes}>
      {/* DEBUG: Temporary debug banner - remove before production */}
      {SHOW_DEBUG_INFO && (
        <div style={debugBannerStyle}>
          <strong>🔍 Feature Grid Debug Info</strong>
          <br />
          <span style={{ color: isPersonalized ? '#155724' : '#856404' }}>
            Mode: {isPersonalized ? '✅ PERSONALIZED' : '📋 RANDOM (default)'}
          </span>
          {debugInfo && (
            <>
              <br />
              <span>
                Refined terms (weight = frequency × recency): {debugInfo.allTerms?.length > 0
                  ? debugInfo.allTerms.map(t => `"${t.keyword}" (w:${t.weight.toFixed(1)})`).join(', ')
                  : 'none'}
              </span>
              {debugInfo.breakdown && (
                <>
                  <br />
                  <span style={{ color: '#155724' }}>
                    📊 Breakdown: <strong>{debugInfo.breakdown.multiMatch}</strong> multi-term match |
                    <strong> {debugInfo.breakdown.singleMatch}</strong> single-term match |
                    <strong> {debugInfo.breakdown.randomFill}</strong> random fill
                  </span>
                </>
              )}
              {debugInfo.topMatches && debugInfo.topMatches.length > 0 && (
                <>
                  <br />
                  <span style={{ fontSize: '12px', color: '#495057' }}>
                    🏆 Top scored listings: {debugInfo.topMatches.slice(0, 3).map(m =>
                      `"${m.title?.substring(0, 25)}${m.title?.length > 25 ? '...' : ''}" [score: ${m.score}, matched ${m.matchCount} term(s): ${m.matchedTerms.join(', ')}]`
                    ).join(' | ')}
                  </span>
                </>
              )}
              {debugInfo.keywordMatchCounts && (
                <>
                  <br />
                  <span style={{ fontSize: '12px', color: '#6c757d' }}>
                    API results per term: {Object.entries(debugInfo.keywordMatchCounts).map(([kw, count]) =>
                      `"${kw}": ${count}`
                    ).join(', ')}
                  </span>
                </>
              )}
              <br />
              <span>Total displayed: {debugInfo.resultsCount ?? gridItems.length}</span>
              {debugInfo.fallbackReason && (
                <>
                  <br />
                  <span style={{ color: '#856404' }}>⚠️ Fallback reason: {debugInfo.fallbackReason}</span>
                </>
              )}
            </>
          )}
          {!debugInfo && !isPersonalized && (
            <>
              <br />
              <span style={{ color: '#6c757d' }}>No search history found for this user</span>
            </>
          )}
        </div>
      )}

      {/* Panel Header */}
      <div className={css.header}>
        {/* Left side - Title */}
        <div className={css.headerLeft}>
          <Heading as="h2" rootClassName={css.title}>
            <FormattedMessage id={titleMessageId} />
          </Heading>
        </div>

        {/* Right side - See all link */}
        <div className={css.headerRight}>
          <NamedLink
            name="SearchPage"
            className={css.seeAllLink}
          >
            <FormattedMessage id="FeatureGridPanel.seeAll" />
          </NamedLink>
        </div>
      </div>

      {/* Grid Container */}
      <div className={css.gridContainer}>
        <div 
          className={css.grid}
          style={{
            '--grid-columns': columns,
            '--grid-rows': rows
          }}
        >
          {displayItems.map((listing) => (
            <div key={listing.id.uuid} className={css.gridItem}>
              <ListingCard
                listing={listing}
                showAuthor={false}
                className={css.listingCard}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FeatureGridPanel;
